/**
 * Document file-type policy for the AI review pipeline.
 *
 * The allowlist is EXACTLY what the processing stack can actually read —
 * Google Document AI (PDF/JPEG/PNG/WebP/GIF/TIFF) and the built-in
 * fallback engines (pdf-parse, Tesseract, mammoth for DOCX). Anything
 * else is refused AT UPLOAD with plain-language copy instead of being
 * accepted into a silently-unreadable state.
 *
 * HEIC/HEIF (the iPhone default) is not readable by Document AI, so it
 * sits in a separate CONVERTIBLE bucket: intake converts it to JPEG
 * server-side (see src/lib/heicConversion.ts) and records the
 * conversion; when a file cannot be converted it is refused with
 * instructions for producing a JPEG.
 *
 * This module is pure and client-safe (no Node imports) — the same
 * policy drives the browser upload checks and the server enforcement.
 */

/** Extensions the review stack can read as-is. */
export const SUPPORTED_DOCUMENT_EXTENSIONS = [
  'pdf', 'jpg', 'jpeg', 'png', 'webp', 'gif', 'tif', 'tiff', 'docx',
] as const;

/** Extensions accepted only via server-side conversion to JPEG. */
export const CONVERTIBLE_DOCUMENT_EXTENSIONS = ['heic', 'heif'] as const;

/** Value for <input accept="..."> on document upload inputs. */
export const DOCUMENT_UPLOAD_ACCEPT =
  '.pdf,.docx,.jpg,.jpeg,.png,.webp,.gif,.tif,.tiff,.heic,.heif';

/** Plain-language copy, shared by server refusals and client guidance. */
export const DOCUMENT_TYPES_GUIDANCE =
  'Accepted file types: PDF, JPEG, PNG, WebP, GIF, TIFF or DOCX. iPhone HEIC photos are converted to JPEG automatically when possible.';

export const HEIC_CONVERSION_GUIDANCE =
  'This photo is in iPhone HEIC format and could not be converted. On the iPhone, open Settings → Camera → Formats and choose "Most Compatible" (JPEG), or open the photo, tap Share and export/save a JPEG copy, then upload that file instead.';

export function extensionOf(fileName: string): string {
  const parts = String(fileName || '').split('.');
  return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
}

export function isSupportedDocumentFileName(fileName: string): boolean {
  return (SUPPORTED_DOCUMENT_EXTENSIONS as readonly string[]).includes(extensionOf(fileName));
}

export function isConvertibleDocumentFileName(fileName: string): boolean {
  return (CONVERTIBLE_DOCUMENT_EXTENSIONS as readonly string[]).includes(extensionOf(fileName));
}

/** May this file be accepted at intake (read directly, or converted)? */
export function isAcceptableDocumentFileName(fileName: string): boolean {
  return isSupportedDocumentFileName(fileName) || isConvertibleDocumentFileName(fileName);
}

// ── Content sniffing (validate by content, not by label) ────────────

export type DetectedFileKind =
  | 'pdf' | 'jpeg' | 'png' | 'gif' | 'tiff' | 'webp' | 'docx-zip' | 'heic' | 'unknown';

const HEIC_BRANDS = new Set([
  'heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'hevs',
  'mif1', 'mif2', 'msf1', 'avif', 'avis',
]);

function ascii(bytes: Uint8Array, from: number, to: number): string {
  let out = '';
  for (let i = from; i < to && i < bytes.length; i++) out += String.fromCharCode(bytes[i]);
  return out;
}

/** Identify a file from its leading bytes (magic numbers). */
export function sniffDocumentKind(bytes: Uint8Array): DetectedFileKind {
  if (!bytes || bytes.length < 4) return 'unknown';
  // PDF: %PDF-
  if (ascii(bytes, 0, 4) === '%PDF') return 'pdf';
  // JPEG: FF D8 FF
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'jpeg';
  // PNG: 89 50 4E 47 0D 0A 1A 0A
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return 'png';
  // GIF: GIF8
  if (ascii(bytes, 0, 4) === 'GIF8') return 'gif';
  // TIFF: II*\0 or MM\0*
  if ((bytes[0] === 0x49 && bytes[1] === 0x49 && bytes[2] === 0x2a && bytes[3] === 0x00) ||
      (bytes[0] === 0x4d && bytes[1] === 0x4d && bytes[2] === 0x00 && bytes[3] === 0x2a)) return 'tiff';
  // WebP: RIFF....WEBP
  if (bytes.length >= 12 && ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 12) === 'WEBP') return 'webp';
  // ISO-BMFF (HEIC/HEIF/AVIF): ....ftyp<brand>
  if (bytes.length >= 12 && ascii(bytes, 4, 8) === 'ftyp' && HEIC_BRANDS.has(ascii(bytes, 8, 12).toLowerCase())) return 'heic';
  // ZIP container (DOCX and friends): PK\x03\x04
  if (bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04) return 'docx-zip';
  return 'unknown';
}

/** The content kind a file extension claims to be (null = unclaimed). */
export function kindForExtension(ext: string): DetectedFileKind | null {
  switch ((ext || '').toLowerCase()) {
    case 'pdf': return 'pdf';
    case 'jpg':
    case 'jpeg': return 'jpeg';
    case 'png': return 'png';
    case 'gif': return 'gif';
    case 'tif':
    case 'tiff': return 'tiff';
    case 'webp': return 'webp';
    case 'docx': return 'docx-zip';
    case 'heic':
    case 'heif': return 'heic';
    default: return null;
  }
}

export interface SignatureContradiction {
  claimedKind: DetectedFileKind;
  detectedKind: DetectedFileKind;
}

/**
 * Non-null when the file's CONTENT contradicts its claimed type
 * (e.g. PNG bytes in a .pdf). 'unknown' content is not a contradiction —
 * it simply cannot be proven either way, and the parsers will fail
 * honestly on it downstream.
 */
export function signatureContradiction(
  bytes: Uint8Array,
  fileName: string
): SignatureContradiction | null {
  const claimed = kindForExtension(extensionOf(fileName));
  if (!claimed) return null;
  const detected = sniffDocumentKind(bytes);
  if (detected === 'unknown' || detected === claimed) return null;
  return { claimedKind: claimed, detectedKind: detected };
}

const KIND_LABELS: Record<DetectedFileKind, string> = {
  pdf: 'PDF',
  jpeg: 'JPEG image',
  png: 'PNG image',
  gif: 'GIF image',
  tiff: 'TIFF image',
  webp: 'WebP image',
  'docx-zip': 'Word/ZIP document',
  heic: 'HEIC photo',
  unknown: 'unknown type',
};

export function fileKindLabel(kind: DetectedFileKind): string {
  return KIND_LABELS[kind] || 'file';
}

/** Refusal copy when content contradicts the claimed type. */
export function signatureMismatchMessage(fileName: string, c: SignatureContradiction): string {
  return `The file "${fileName}" does not match its type: its contents look like a ${fileKindLabel(c.detectedKind)}, not a ${fileKindLabel(c.claimedKind)}. Please re-export or re-save it in the correct format and upload it again. ${DOCUMENT_TYPES_GUIDANCE}`;
}

/** Refusal copy for a type the review stack cannot read at all. */
export function unsupportedTypeMessage(fileName: string): string {
  return `Unsupported file type for "${fileName}". ${DOCUMENT_TYPES_GUIDANCE}`;
}
